import { Avatar, type AvatarUser } from "@/components/ui/Avatar";
import { CountPill } from "@/components/memo/ui/CountPill";
import { Icon } from "@/components/ui/Icon";
import { MemoMarkdown } from "@/components/memo/MemoMarkdown";
import { memoUserToAvatar } from "@/lib/memoAvatar";
import { newSectionsForBlock } from "@/lib/memoNewEntries";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";
import type { MemoBlock, MemoEntry, MemoUser } from "@/lib/types";

interface Props {
  blocks: MemoBlock[];
  /** Assignee faces — the SAME pool the memo header/mentions use (lib/memoPeople). */
  people: Map<string, MemoUser>;
  /** Abschnitt accent bar colour (workspace colour), mirroring MemoBlockView. */
  accentColor: string;
}

/** "Fällig DD.MM." for the Frist pill — mirrors MemoEntryRow's fmtDate. */
function fmtDate(iso: string): string {
  const [, mo, d] = iso.split("-");
  return `${d}.${mo}.`;
}

const todayIso = new Date().toISOString().slice(0, 10);

/**
 * The "Nur Neues" glance: under each Zwischenüberschrift, only the points ADDED
 * this meeting (see lib/memoNewEntries). Read-only — carried-forward points, the
 * "frühere Punkte" reveals and every editing affordance are gone; a small orange
 * dot marks each new point. Sections with no new points are dropped entirely.
 */
export function MemoNewOnlyView({ blocks, people, accentColor }: Props) {
  const views = blocks
    .map((block) => ({ block, sections: newSectionsForBlock(block) }))
    .filter((v) => v.sections.length > 0);

  if (views.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 12,
          padding: "56px 24px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 52,
            height: 52,
            borderRadius: 999,
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
          }}
        >
          <Icon name="star" size={22} color="var(--ink-4)" />
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-2)" }}>
          {t.memoNewOnlyEmptyTitle}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: "var(--ink-3)", maxWidth: 330 }}>
          {t.memoNewOnlyEmptyBody}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Filter caption */}
      <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{t.memoNewOnlyCaption}</div>

      {views.map(({ block, sections }) => (
        <div key={block.id}>
          {/* Abschnitt heading — mirrors MemoBlockView's colour bar + h3 */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <span
              style={{
                width: 6,
                height: 18,
                borderRadius: 2,
                background: accentColor,
                flexShrink: 0,
              }}
            />
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--ink-1)",
                margin: 0,
              }}
            >
              {block.title}
            </h3>
          </div>

          {sections.map((section, i) => (
            <div key={section.anchorId} style={{ marginLeft: 16, marginTop: i === 0 ? 0 : 22 }}>
              {section.heading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      width: 6,
                      height: 15,
                      borderRadius: 2,
                      background: "var(--ink-3)",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "var(--ink-1)",
                    }}
                  >
                    {section.heading.body}
                  </span>
                </div>
              ) : null}
              <div
                style={{
                  marginLeft: section.heading ? 18 : 0,
                  marginTop: section.heading ? 10 : 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 13,
                }}
              >
                {section.entries.map((entry) => (
                  <NewPoint key={entry.id} entry={entry} people={people} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function NewPoint({ entry, people }: { entry: MemoEntry; people: Map<string, MemoUser> }) {
  const isTask = Boolean(entry.assignee_user_id) || Boolean(entry.due_date);
  const assignee = entry.assignee_user_id ? (people.get(entry.assignee_user_id) ?? null) : null;
  const assigneeName = assignee
    ? (assignee.display_name ?? assignee.username ?? "?")
    : entry.assignee_user_id
      ? "?"
      : null;
  const overdue = entry.due_date ? entry.due_date < todayIso : false;
  const avatar: AvatarUser | null = assignee ? memoUserToAvatar(assignee) : null;
  // If the body IS a markdown list it brings its own bullets — drop the gutter
  // dot so there is no double bullet, exactly like MemoEntryRow. Prose keeps a
  // single dot (blue for a to-do, grey for a note), matching the live view.
  const firstBodyLine = entry.body.split("\n").find((l) => l.trim()) ?? "";
  const startsWithList = /^\s*([-*+]|\d+[.)])\s/.test(firstBodyLine);

  return (
    <div style={{ display: "flex", gap: 11 }}>
      <span
        style={{
          width: 6,
          marginTop: 7,
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {startsWithList ? null : (
          <span
            data-testid="memo-new-dot"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: isTask ? BRAND.blue : "var(--ink-3)",
            }}
          />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-1)" }}>
          <MemoMarkdown markdown={entry.body} />
        </div>
        {isTask ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            {assigneeName ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  color: "var(--ink-1)",
                  background: "var(--surface-soft)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 8,
                  padding: "3px 9px",
                }}
              >
                {avatar ? (
                  <Avatar user={avatar} size={20} />
                ) : (
                  <Icon name="person" size={13} color="var(--accent)" />
                )}
                {assigneeName}
              </span>
            ) : null}
            {entry.due_date ? (
              <CountPill tone={overdue ? "overdue" : "neutral"}>
                <Icon name="date" size={12} />
                bis {fmtDate(entry.due_date)}
              </CountPill>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
